import React, { useState, useMemo } from 'react';
import { Modal, Tabs, Card, Row, Col, Tag, Divider, Collapse, Empty, Badge, Button } from 'antd';
import {
    QuestionCircleOutlined,
    DashboardOutlined,
    UserOutlined,
    TeamOutlined,
    CalendarOutlined,
    MedicineBoxOutlined,
    FileTextOutlined,
    DollarOutlined,
    BankOutlined,
    BarChartOutlined,
    AppstoreOutlined,
    ShoppingCartOutlined,
    ImportOutlined,
    UnorderedListOutlined,
    RollbackOutlined,
    CameraOutlined,
    ExperimentOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './SystemGuideModal.css';

const SystemGuideModal = ({ isOpen, onClose }) => {
    const { t, i18n } = useTranslation(['common', 'guide']);
    const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

    const systemSections = useMemo(() => [
        {
            key: 'dashboard',
            title: t('guide:sections.dashboard.title', 'Dashboard'),
            icon: <DashboardOutlined />,
            description: t('guide:sections.dashboard.description', 'Get a quick overview of your clinic operations.'),
            details: t('guide:sections.dashboard.details', 'View key metrics, upcoming appointments, recent activities, and clinic statistics at a glance.')
        },
        {
            key: 'patients',
            title: t('guide:sections.patients.title', 'Patients'),
            icon: <UserOutlined />,
            description: t('guide:sections.patients.description', 'Manage patient information and medical records.'),
            details: t('guide:sections.patients.details', 'Create, update, and view patient profiles including contact information, medical history, treatments, and prescriptions.')
        },
        {
            key: 'appointments',
            title: t('guide:sections.appointments.title', 'Appointments'),
            icon: <CalendarOutlined />,
            description: t('guide:sections.appointments.description', 'Schedule and manage dental appointments.'),
            details: t('guide:sections.appointments.details', 'Book, reschedule, or cancel appointments with patients. Track appointment status, duration, and associated notes.')
        },
        {
            key: 'treatments',
            title: t('guide:sections.treatments.title', 'Treatments'),
            icon: <MedicineBoxOutlined />,
            description: t('guide:sections.treatments.description', 'Record treatment procedures and clinical notes.'),
            details: t('guide:sections.treatments.details', 'Document treatment plans, procedures performed, tooth charts, clinical observations, and treatment costs.')
        },
        {
            key: 'prescriptions',
            title: t('guide:sections.prescriptions.title', 'Prescriptions'),
            icon: <FileTextOutlined />,
            description: t('guide:sections.prescriptions.description', 'Manage patient medications and prescriptions.'),
            details: t('guide:sections.prescriptions.details', 'Create prescriptions for patients, track medication history, and manage pharmacy interactions.')
        },
        {
            key: 'payments',
            title: t('guide:sections.payments.title', 'Payments'),
            icon: <DollarOutlined />,
            description: t('guide:sections.payments.description', 'Handle patient billing and payment tracking.'),
            details: t('guide:sections.payments.details', 'Record payments, generate invoices, track outstanding bills, and view payment history.')
        },
        {
            key: 'expenses',
            title: t('guide:sections.expenses.title', 'Expenses'),
            icon: <BankOutlined />,
            description: t('guide:sections.expenses.description', 'Track clinic operational expenses.'),
            details: t('guide:sections.expenses.details', 'Record utility bills, supplier payments, staff expenses, equipment costs, and generate expense reports.')
        },
        {
            key: 'reports',
            title: t('guide:sections.reports.title', 'Reports'),
            icon: <BarChartOutlined />,
            description: t('guide:sections.reports.description', 'Generate analytics and business insights.'),
            details: t('guide:sections.reports.details', 'View financial reports, patient statistics, revenue analysis, appointment trends, and clinic performance metrics.')
        },
        {
            key: 'pharmacy',
            title: t('guide:sections.pharmacy.title', 'Pharmacy'),
            icon: <AppstoreOutlined />,
            description: t('guide:sections.pharmacy.description', 'Manage pharmacy inventory and sales.'),
            details: t('guide:sections.pharmacy.details', 'Track medication inventory, process sales, manage purchases, handle returns, and generate pharmacy sales reports.')
        },
        {
            key: 'laboratory',
            title: t('guide:sections.laboratory.title', 'Laboratory'),
            icon: <ExperimentOutlined />,
            description: t('guide:sections.laboratory.description', 'Manage lab orders and dental prosthetics.'),
            details: t('guide:sections.laboratory.details', 'Create lab orders, track prosthetic work, manage lab invoices, and handle lab result submissions.')
        },
        {
            key: 'radiology',
            title: t('guide:sections.radiology.title', 'Radiology'),
            icon: <CameraOutlined />,
            description: t('guide:sections.radiology.description', 'Manage X-rays and radiological images.'),
            details: t('guide:sections.radiology.details', 'Upload and organize X-ray images, attach them to patient records, manage radiology reports, and track imaging history.')
        },
        {
            key: 'dentists',
            title: t('guide:sections.dentists.title', 'Dentists'),
            icon: <TeamOutlined />,
            description: t('guide:sections.dentists.description', 'Manage dentist staff profiles.'),
            details: t('guide:sections.dentists.details', 'View dentist details, qualifications, specializations, consultation fees, and availability schedules.')
        }
    ], [t, i18n.language]);

    const userRoles = useMemo(() => [
        // {
        //     role: 'super_admin',
        //     title: t('guide:roles.super_admin.title', 'Super Administrator'),
        //     color: '#FF4D4F',
        //     description: t('guide:roles.super_admin.description', 'Manages multiple clinics and system-wide settings.'),
        //     permissions: [
        //         t('guide:permissions.manage_clinics', 'Manage multiple clinics'),
        //         t('guide:permissions.view_all_clinics', 'View all clinics data'),
        //         t('guide:permissions.manage_users', 'Manage all users'),
        //         t('guide:permissions.manage_settings', 'Manage system settings'),
        //         t('guide:permissions.view_reports', 'View all reports')
        //     ]
        // },
        {
            role: 'admin',
            title: t('guide:roles.admin.title', 'Clinic Admin'),
            color: '#1890FF',
            description: t('guide:roles.admin.description', 'Full access to clinic management and staff control.'),
            permissions: [
                t('guide:permissions.manage_users', 'Manage clinic users and staff'),
                t('guide:permissions.manage_patients', 'Manage patient records'),
                t('guide:permissions.manage_appointments', 'Manage appointments'),
                t('guide:permissions.manage_treatments', 'Manage treatments'),
                t('guide:permissions.manage_prescriptions', 'Manage prescriptions'),
                t('guide:permissions.manage_payments', 'Handle payments and billing'),
                t('guide:permissions.view_reports', 'View clinic reports'),
                t('guide:permissions.manage_settings', 'Configure clinic settings'),
                t('guide:permissions.manage_inventory', 'Manage pharmacy inventory')
            ]
        },
        {
            role: 'dentist',
            title: t('guide:roles.dentist.title', 'Dentist'),
            color: '#52C41A',
            description: t('guide:roles.dentist.description', 'Provides clinical care and manages patient treatments.'),
            permissions: [
                t('guide:permissions.manage_patients', 'View and manage their patients'),
                t('guide:permissions.manage_appointments', 'Manage appointments'),
                t('guide:permissions.manage_treatments', 'Document treatments'),
                t('guide:permissions.manage_prescriptions', 'Create prescriptions'),
                t('guide:permissions.manage_payments', 'View payments'),
                t('guide:permissions.view_reports', 'View patient reports')
            ]
        },
        {
            role: 'receptionist',
            title: t('guide:roles.receptionist.title', 'Receptionist / Secretary'),
            color: '#FAAD14',
            description: t('guide:roles.receptionist.description', 'Handles patient scheduling and front-desk operations.'),
            permissions: [
                t('guide:permissions.manage_appointments', 'Schedule and manage appointments'),
                t('guide:permissions.manage_patients', 'Create and view patient records'),
                t('guide:permissions.view_patients', 'Access patient contact information')
            ]
        },
        {
            role: 'pharmacy',
            title: t('guide:roles.pharmacy.title', 'Pharmacy Technician'),
            color: '#722ED1',
            description: t('guide:roles.pharmacy.description', 'Manages pharmacy inventory and medication sales.'),
            permissions: [
                t('guide:permissions.manage_inventory', 'Manage medication inventory'),
                t('guide:permissions.view_prescriptions', 'View patient prescriptions'),
                t('guide:permissions.create_pharmacy_invoices', 'Create pharmacy invoices'),
                t('guide:permissions.view_pharmacy_reports', 'View pharmacy reports')
            ]
        },
        {
            role: 'lab_technician',
            title: t('guide:roles.lab_technician.title', 'Lab Technician'),
            color: '#13C2C2',
            description: t('guide:roles.lab_technician.description', 'Manages dental lab work and prosthetics.'),
            permissions: [
                t('guide:permissions.view_lab_orders', 'View lab orders'),
                t('guide:permissions.manage_lab_work', 'Update lab work status'),
                t('guide:permissions.create_lab_invoices', 'Create lab invoices'),
                t('guide:permissions.view_lab_reports', 'View lab reports')
            ]
        },
        {
            role: 'radiologist',
            title: t('guide:roles.radiologist.title', 'Radiologist'),
            color: '#EB2F96',
            description: t('guide:roles.radiologist.description', 'Manages X-rays and radiological imaging.'),
            permissions: [
                t('guide:permissions.upload_xrays', 'Upload and manage X-ray images'),
                t('guide:permissions.create_radiology_invoices', 'Create radiology invoices'),
                t('guide:permissions.view_radiology_reports', 'View radiology reports')
            ]
        }
    ], [t, i18n.language]);

    const tabItems = useMemo(() => [
        {
            key: 'sections',
            label: t('guide:tabs.sections', 'System Sections'),
            children: (
                <div className="guide-content">
                    <Row gutter={[16, 16]}>
                        {systemSections.map((section) => (
                            <Col key={section.key} xs={24} sm={24} md={12} lg={8} xl={8}>
                                <Card
                                    hoverable
                                    className="section-card"
                                    style={{
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                        height: '100%'
                                    }}
                                >
                                    <div style={{ fontSize: '24px', marginBottom: '8px', color: '#5f7868' }}>
                                        {section.icon}
                                    </div>
                                    <h4 style={{ margin: '8px 0 4px 0', color: '#001529' }}>
                                        {section.title}
                                    </h4>
                                    <p style={{ color: '#666', fontSize: '12px', marginBottom: '8px' }}>
                                        {section.description}
                                    </p>
                                    <p style={{ color: '#999', fontSize: '11px' }}>
                                        {section.details}
                                    </p>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            )
        },
        {
            key: 'roles',
            label: t('guide:tabs.roles', 'User Roles'),
            children: (
                <div className="guide-content">
                    <Collapse
                        items={userRoles.map((roleInfo) => ({
                            key: roleInfo.role,
                            label: (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Tag color={roleInfo.color} style={{ margin: 0 }}>
                                        {roleInfo.title}
                                    </Tag>
                                    <span style={{ color: '#666', fontSize: '12px' }}>
                                        {roleInfo.description}
                                    </span>
                                </div>
                            ),
                            children: (
                                <div>
                                    <p style={{ marginBottom: '12px', color: '#666' }}>
                                        {roleInfo.description}
                                    </p>
                                    <div style={{ marginBottom: '12px' }}>
                                        <h5 style={{ marginBottom: '8px', color: '#001529', fontWeight: 'bold' }}>
                                            {t('guide:permissions.canAccess', 'Can Access & Manage:')}
                                        </h5>
                                        <ul style={{ paddingLeft: '20px', margin: 0 }}>
                                            {roleInfo.permissions.map((permission, idx) => (
                                                <li key={idx} style={{ color: '#666', marginBottom: '4px' }}>
                                                    {permission}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <Tag color={roleInfo.color} style={{ marginTop: '8px' }}>
                                        {t('guide:roles.accessLevel', 'Access Level')}: {roleInfo.title}
                                    </Tag>
                                </div>
                            )
                        }))}
                    />
                </div>
            )
        },
        {
            key: 'workflow',
            label: t('guide:tabs.workflow', 'Common Workflows'),
            children: (
                <div className="guide-content">
                    <Collapse
                        items={[
                            {
                                key: 'patient_visit',
                                label: t('guide:workflows.patientVisit.title', 'Patient Visit Workflow'),
                                children: (
                                    <ol style={{ paddingLeft: '20px' }}>
                                        <li>{t('guide:workflows.patientVisit.step1', 'Receptionist schedules appointment')}</li>
                                        <li>{t('guide:workflows.patientVisit.step2', 'Patient arrives at clinic')}</li>
                                        <li>{t('guide:workflows.patientVisit.step3', 'Dentist reviews patient history and performs examination')}</li>
                                        <li>{t('guide:workflows.patientVisit.step4', 'Treatment is documented with clinical notes')}</li>
                                        <li>{t('guide:workflows.patientVisit.step5', 'Prescription issued if needed')}</li>
                                        <li>{t('guide:workflows.patientVisit.step6', 'Admin processes payment')}</li>
                                        <li>{t('guide:workflows.patientVisit.step7', 'Patient receives follow-up appointment (if needed)')}</li>
                                    </ol>
                                )
                            },
                            {
                                key: 'lab_order',
                                label: t('guide:workflows.labOrder.title', 'Lab Order Workflow'),
                                children: (
                                    <ol style={{ paddingLeft: '20px' }}>
                                        <li>{t('guide:workflows.labOrder.step1', 'Dentist creates lab order from treatment')}</li>
                                        <li>{t('guide:workflows.labOrder.step2', 'Lab technician receives the order')}</li>
                                        <li>{t('guide:workflows.labOrder.step3', 'Lab work progresses through different stages')}</li>
                                        <li>{t('guide:workflows.labOrder.step4', 'Completed work is marked as ready')}</li>
                                        <li>{t('guide:workflows.labOrder.step5', 'Dentist reviews and installs prosthetic')}</li>
                                        <li>{t('guide:workflows.labOrder.step6', 'Invoice is generated and payment is processed')}</li>
                                    </ol>
                                )
                            },
                            {
                                key: 'xray_imaging',
                                label: t('guide:workflows.xrayImaging.title', 'X-Ray & Imaging Workflow'),
                                children: (
                                    <ol style={{ paddingLeft: '20px' }}>
                                        <li>{t('guide:workflows.xrayImaging.step1', 'Dentist requests X-ray during treatment')}</li>
                                        <li>{t('guide:workflows.xrayImaging.step2', 'Radiologist takes and uploads images')}</li>
                                        <li>{t('guide:workflows.xrayImaging.step3', 'Images are automatically linked to patient')}</li>
                                        <li>{t('guide:workflows.xrayImaging.step4', 'Dentist reviews images and adds radiological findings')}</li>
                                        <li>{t('guide:workflows.xrayImaging.step5', 'Images are stored for future reference')}</li>
                                    </ol>
                                )
                            },
                            {
                                key: 'pharmacy_sales',
                                label: t('guide:workflows.pharmacySales.title', 'Pharmacy Sales Workflow'),
                                children: (
                                    <ol style={{ paddingLeft: '20px' }}>
                                        <li>{t('guide:workflows.pharmacySales.step1', 'Patient receives prescription from dentist')}</li>
                                        <li>{t('guide:workflows.pharmacySales.step2', 'Pharmacy checks inventory for medications')}</li>
                                        <li>{t('guide:workflows.pharmacySales.step3', 'Medication is prepared and dispensed')}</li>
                                        <li>{t('guide:workflows.pharmacySales.step4', 'Sale invoice is created')}</li>
                                        <li>{t('guide:workflows.pharmacySales.step5', 'Payment is received from patient')}</li>
                                        <li>{t('guide:workflows.pharmacySales.step6', 'Inventory is updated automatically')}</li>
                                    </ol>
                                )
                            }
                        ]}
                    />
                </div>
            )
        }
    ], [t, i18n.language]);

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <QuestionCircleOutlined style={{ color: '#5f7868' }} />
                    <span>{t('guide:title', 'System Guide & Help')}</span>
                </div>
            }
            open={isOpen}
            onCancel={onClose}
            width={1200}
            footer={[
                <Button key="close" onClick={onClose}>
                    {t('guide:close', 'Close')}
                </Button>
            ]}
            bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
            centered
            className={isRTL ? 'rtl-modal' : ''}
        >
            <Tabs items={tabItems} />
        </Modal>
    );
};

export default SystemGuideModal;
